import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, FileText } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { downloadCaseFile, useCaseFiles } from "@/api/queries/cases";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { falteDateiVersionen, formatBytes } from "./fileVersions";
import type { CaseFileEntry, CaseStepFold } from "@/types";

// #303 (UC-2, Bau-Nachtrag): die Abgaben des Laufs mit Versions-Faltung.
//
// Der Agent gibt eine überarbeitete Datei unter DEMSELBEN Namen erneut ab — jede Abgabe
// bleibt eine eigene, unveränderliche Kopie. Ohne Faltung stünden hier fünf Zeilen
// „bericht.md", und niemand wüsste, welche gilt. Mit Faltung: EINE Zeile „aktuell", die
// älteren Fassungen aufklappbar darunter.

function Zeile({
  datei,
  caseId,
  schrittLabel,
}: {
  datei: CaseFileEntry;
  caseId: string;
  schrittLabel: string | null;
}) {
  const { session } = useAuth();
  const lade = () => {
    if (!session) return;
    void downloadCaseFile(session.accessToken, caseId, datei.attachmentId, datei.filename).catch(() =>
      toast.error("Datei konnte nicht geladen werden"),
    );
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px] text-text2">
      <span className="font-mono text-[11px] text-text3">Fassung {datei.version}</span>
      {schrittLabel && <span className="text-text3">aus „{schrittLabel}"</span>}
      <span className="text-text3">{new Date(datei.deliveredAt).toLocaleString("de-CH")}</span>
      <Button size="sm" variant="ghost" onClick={lade} data-testid="datei-download-alt">
        <Download className="h-3.5 w-3.5" />
        Herunterladen
      </Button>
    </div>
  );
}

export function CaseFilesCard({ caseId, steps }: { caseId: string; steps: CaseStepFold[] }) {
  const files = useCaseFiles(caseId);
  const { session } = useAuth();
  const [aufgeklappt, setAufgeklappt] = useState<Record<string, boolean>>({});

  const ketten = useMemo(() => falteDateiVersionen(files.data ?? []), [files.data]);
  // Klarnamen der Schritte — sonst stünde an einer Fassung nur eine technische Id.
  const schrittName = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of steps) m.set(s.id, s.name ?? s.id);
    return m;
  }, [steps]);

  if (files.isError) {
    return (
      <SectionCard title="Dateien">
        <p className="text-[12.5px] text-danger">Dateien nicht abrufbar.</p>
      </SectionCard>
    );
  }
  if (ketten.length === 0) return null;

  const lade = (d: CaseFileEntry) => {
    if (!session) return;
    void downloadCaseFile(session.accessToken, caseId, d.attachmentId, d.filename).catch(() =>
      toast.error("Datei konnte nicht geladen werden"),
    );
  };

  return (
    <SectionCard
      title="Dateien"
      description="Abgaben des Laufs. Gleicher Name = neue Fassung derselben Datei."
      testId="case-files"
    >
      <div className="divide-y divide-border">
        {ketten.map((k) => {
          const offen = aufgeklappt[k.filename] === true;
          const schritt = k.aktuell.stepId ? (schrittName.get(k.aktuell.stepId) ?? k.aktuell.stepId) : null;
          return (
            <div key={k.filename} className="py-2" data-testid="datei-kette">
              <div className="flex flex-wrap items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-text3" />
                <span className="min-w-0 break-all text-[13px] font-semibold text-text">{k.filename}</span>
                <Badge variant="active">aktuell</Badge>
                {k.aktuell.version > 1 && (
                  <span className="text-[11.5px] text-text3">Fassung {k.aktuell.version}</span>
                )}
                <span className="text-[11.5px] text-text3">{formatBytes(k.aktuell.size)}</span>
                {schritt && <span className="text-[11.5px] text-text3">aus „{schritt}"</span>}
                <Button size="sm" variant="outline" onClick={() => lade(k.aktuell)} data-testid="datei-download">
                  <Download className="h-3.5 w-3.5" />
                  Herunterladen
                </Button>
              </div>
              {k.aeltere.length > 0 && (
                <div className="mt-1 pl-6">
                  <button
                    type="button"
                    onClick={() => setAufgeklappt((a) => ({ ...a, [k.filename]: !offen }))}
                    className="inline-flex items-center gap-1 text-[12px] text-accent hover:underline"
                    data-testid="datei-versionen-toggle"
                  >
                    {offen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    {k.aeltere.length} ältere {k.aeltere.length === 1 ? "Fassung" : "Fassungen"}
                  </button>
                  {offen && (
                    <div className="mt-1 space-y-1" data-testid="datei-versionen">
                      {k.aeltere.map((d) => (
                        <Zeile
                          key={d.attachmentId}
                          datei={d}
                          caseId={caseId}
                          schrittLabel={d.stepId ? (schrittName.get(d.stepId) ?? d.stepId) : null}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
